import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  changeSettingsQuery,
  removeCabGroup,
  removeRiskQuestion,
  saveCabGroup,
  saveRiskQuestion,
  saveRiskThresholds,
  type CabGroup,
  type ChangeSettingsPage,
  type RiskQuestion,
} from '../../api/changeManagement'
import { ApiError } from '../../api/client'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Change management (cerinta CEC L30, L31).
 *
 * Trei lucruri ale furnizorului, pe care le folosesc cererile de schimbare:
 *
 *   - GRUPURILE CAB - cine aproba. Grupul si regula (toti / majoritate /
 *     oricare) se aleg pe pasul de flux, in Workflows;
 *   - CHESTIONARUL DE RISC - intrebari cu raspunsuri punctate, completat pe
 *     fisa change-ului la cerere;
 *   - PRAGURILE - de la cate puncte riscul e mediu, de la cate e mare.
 *
 * Ce tipuri de tichet sunt change-uri se alege in Ticket types, langa tip.
 */

function failed(error: unknown) {
  toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
}

export function ChangeManagementPage() {
  const page = useQuery(changeSettingsQuery())
  const queryClient = useQueryClient()
  const reload = () => void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'change_management'] })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <LoadFailed what="Change management" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  return (
    <div className="od-change-settings">
      <SettingsHead
        strong="Change"
        rest="management"
        subtitle={
          <>
            Who approves a change (<strong>CAB</strong>), and how its <strong>risk</strong> is assessed. Mark a ticket
            type as a change under <a href="/app/settings/easeedesk/ticket_types_settings">Ticket types</a>; put a CAB
            group on the approval step under <a href="/app/settings/easeedesk/workflow_settings">Workflows</a>. The
            meetings of the CAB, with their agenda and minutes, are under{' '}
            <a href="/app/easeedesk/cab_meetings">CAB meetings</a>.
          </>
        }
      />

      <CabGroups page={page.data} onSaved={reload} />
      <Questionnaire page={page.data} onSaved={reload} />
      <Thresholds page={page.data} onSaved={reload} />
    </div>
  )
}

/* ------------------------------------------------------------------ */

function CabGroups({ page, onSaved }: { page: ChangeSettingsPage; onSaved(): void }) {
  const [editing, setEditing] = useState<CabGroup | null>(null)

  const remove = useMutation({
    mutationFn: (id: number) => removeCabGroup(id),
    onSuccess: (answer) => {
      toast(answer.message, 'Change management')
      onSaved()
    },
    onError: failed,
  })

  return (
    <section className="od-change-section">
      <h5>
        <strong>CAB groups</strong>{' '}
        <button
          type="button"
          className="btn btn-success btn-xs od-cab-add"
          onClick={() => setEditing({ ID: 0, name: '', description: '', members: [] })}
        >
          <i className="fas fa-plus" aria-hidden="true"></i> Add a CAB group
        </button>
      </h5>

      {page.groups.length === 0 ? (
        <p className="color-light-grey">No CAB group yet. Without one, no workflow step can ask for approval.</p>
      ) : (
        <table className="table od-cab-groups">
          <thead>
            <tr>
              <th>Group</th>
              <th>Members</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {page.groups.map((group) => (
              <tr key={group.ID}>
                <td>
                  <strong>{group.name}</strong>
                  {group.description !== '' && <div className="color-light-grey">{group.description}</div>}
                </td>
                <td>
                  {group.members.length === 0 ? (
                    <span className="text-danger">nobody - it cannot approve anything</span>
                  ) : (
                    group.members.map((m) => m.name).join(', ')
                  )}
                </td>
                <td className="aright" style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="btn btn-default btn-xs od-cab-edit" onClick={() => setEditing(group)}>
                    <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                  </button>{' '}
                  <button
                    type="button"
                    className="btn btn-default btn-xs od-cab-remove"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(group.ID)}
                  >
                    <i className="fas fa-times" aria-hidden="true"></i> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <GroupForm
          key={editing.ID}
          group={editing}
          staff={page.staff}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onSaved()
          }}
        />
      )}
    </section>
  )
}

function GroupForm({
  group,
  staff,
  onClose,
  onSaved,
}: {
  group: CabGroup
  staff: { ID: number; name: string }[]
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description)
  const [members, setMembers] = useState<number[]>(group.members.map((m) => m.ID))

  const save = useMutation({
    mutationFn: () => saveCabGroup(group.ID, { name, description, members }),
    onSuccess: (answer) => {
      toast(answer.message, 'Change management')
      onSaved()
    },
    onError: failed,
  })

  return (
    <form
      className="well od-cab-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim() !== '') save.mutate()
      }}
    >
      <div className="row">
        <div className="col-sm-4 form-group">
          <label className="control-label" htmlFor="cabName">
            Name <span className="required">*</span>
          </label>
          <input id="cabName" className="form-control" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-sm-8 form-group">
          <label className="control-label" htmlFor="cabDescription">
            Description
          </label>
          <input
            id="cabDescription"
            className="form-control"
            maxLength={255}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
      <div className="form-group">
        <label className="control-label">Members</label>
        <div className="od-cab-members">
          {staff.map((person) => (
            <label key={person.ID} className="checkbox-inline" style={{ marginLeft: 0, marginRight: 14 }}>
              <input
                type="checkbox"
                checked={members.includes(person.ID)}
                onChange={(e) =>
                  setMembers(e.target.checked ? [...members, person.ID] : members.filter((id) => id !== person.ID))
                }
              />{' '}
              {person.name}
            </label>
          ))}
        </div>
      </div>
      <div className="aright">
        <button type="button" className="btn btn-default btn-sm" onClick={onClose}>
          Cancel
        </button>{' '}
        <button type="submit" className="btn btn-primary btn-sm od-cab-save" disabled={name.trim() === '' || save.isPending}>
          Save the group
        </button>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */

const BLANK_QUESTION: RiskQuestion = {
  ID: 0,
  question: '',
  options: [
    { label: '', score: 0 },
    { label: '', score: 0 },
  ],
  sort_order: 0,
}

function Questionnaire({ page, onSaved }: { page: ChangeSettingsPage; onSaved(): void }) {
  const [editing, setEditing] = useState<RiskQuestion | null>(null)

  const remove = useMutation({
    mutationFn: (id: number) => removeRiskQuestion(id),
    onSuccess: (answer) => {
      toast(answer.message, 'Change management')
      onSaved()
    },
    onError: failed,
  })

  const most = page.questions.reduce((sum, q) => sum + Math.max(0, ...q.options.map((o) => o.score)), 0)

  return (
    <section className="od-change-section">
      <h5>
        <strong>Risk questionnaire</strong>{' '}
        <button
          type="button"
          className="btn btn-success btn-xs od-question-add"
          onClick={() => setEditing({ ...BLANK_QUESTION, sort_order: page.questions.length + 1 })}
        >
          <i className="fas fa-plus" aria-hidden="true"></i> Add a question
        </button>
      </h5>

      {page.questions.length === 0 ? (
        <p className="color-light-grey">No questions yet. A change can still be given a risk by hand.</p>
      ) : (
        <>
          <ol className="od-questions">
            {page.questions.map((q) => (
              <li key={q.ID}>
                <strong>{q.question}</strong>{' '}
                <span className="color-light-grey">{q.options.map((o) => `${o.label} (${o.score})`).join(' · ')}</span>{' '}
                <button type="button" className="btn btn-link btn-xs od-question-edit" onClick={() => setEditing(q)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-xs od-question-remove"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(q.ID)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ol>
          <p className="color-light-grey">The highest possible score is {most} points.</p>
        </>
      )}

      {editing && (
        <QuestionForm
          key={editing.ID}
          question={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onSaved()
          }}
        />
      )}
    </section>
  )
}

function QuestionForm({ question, onClose, onSaved }: { question: RiskQuestion; onClose(): void; onSaved(): void }) {
  const [text, setText] = useState(question.question)
  const [options, setOptions] = useState(question.options)
  const filled = options.filter((o) => o.label.trim() !== '')

  const save = useMutation({
    mutationFn: () => saveRiskQuestion(question.ID, { question: text, options: filled, sort_order: question.sort_order }),
    onSuccess: (answer) => {
      toast(answer.message, 'Change management')
      onSaved()
    },
    onError: failed,
  })

  const setOption = (index: number, next: { label: string; score: number }) =>
    setOptions(options.map((o, i) => (i === index ? next : o)))

  return (
    <form
      className="well od-question-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (text.trim() !== '' && filled.length >= 2) save.mutate()
      }}
    >
      <div className="form-group">
        <label className="control-label" htmlFor="riskQuestion">
          Question <span className="required">*</span>
        </label>
        <input id="riskQuestion" className="form-control" maxLength={255} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <label className="control-label">Answers and their points</label>
      {options.map((option, index) => (
        <div className="row od-question-option" key={index} style={{ marginBottom: 4 }}>
          <div className="col-xs-8">
            <input
              className="form-control input-sm"
              aria-label={`Answer ${index + 1}`}
              placeholder={`Answer ${index + 1}`}
              maxLength={120}
              value={option.label}
              onChange={(e) => setOption(index, { ...option, label: e.target.value })}
            />
          </div>
          <div className="col-xs-3">
            <input
              type="number"
              min={0}
              max={100}
              className="form-control input-sm"
              aria-label={`Points for answer ${index + 1}`}
              value={option.score}
              onChange={(e) => setOption(index, { ...option, score: Number(e.target.value) })}
            />
          </div>
          <div className="col-xs-1">
            {options.length > 2 && (
              <button
                type="button"
                className="btn btn-link btn-xs"
                aria-label={`Remove answer ${index + 1}`}
                onClick={() => setOptions(options.filter((_, i) => i !== index))}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="aright">
        {options.length < 10 && (
          <button type="button" className="btn btn-default btn-sm pull-left" onClick={() => setOptions([...options, { label: '', score: 0 }])}>
            <i className="fas fa-plus" aria-hidden="true"></i> Answer
          </button>
        )}
        <button type="button" className="btn btn-default btn-sm" onClick={onClose}>
          Cancel
        </button>{' '}
        <button
          type="submit"
          className="btn btn-primary btn-sm od-question-save"
          disabled={text.trim() === '' || filled.length < 2 || save.isPending}
        >
          Save the question
        </button>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */

function Thresholds({ page, onSaved }: { page: ChangeSettingsPage; onSaved(): void }) {
  const [medium, setMedium] = useState(page.thresholds.medium_from)
  const [high, setHigh] = useState(page.thresholds.high_from)

  const save = useMutation({
    mutationFn: () => saveRiskThresholds(medium, high),
    onSuccess: (answer) => {
      toast(answer.message, 'Change management')
      onSaved()
    },
    onError: failed,
  })

  return (
    <section className="od-change-section">
      <h5>
        <strong>Risk levels</strong>
      </h5>
      <form
        className="form-inline od-thresholds"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <label htmlFor="riskMedium">Medium from</label>{' '}
        <input
          id="riskMedium"
          type="number"
          min={1}
          className="form-control input-sm"
          style={{ width: 80 }}
          value={medium}
          onChange={(e) => setMedium(Number(e.target.value))}
        />{' '}
        <label htmlFor="riskHigh">high from</label>{' '}
        <input
          id="riskHigh"
          type="number"
          min={2}
          className="form-control input-sm"
          style={{ width: 80 }}
          value={high}
          onChange={(e) => setHigh(Number(e.target.value))}
        />{' '}
        <span className="color-light-grey">points</span>{' '}
        <button type="submit" className="btn btn-primary btn-sm od-thresholds-save" disabled={save.isPending || high <= medium || medium < 1}>
          Save
        </button>
      </form>
    </section>
  )
}
